import PropTypes from 'prop-types'
import { useState } from 'react'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  Button,
  Checkbox,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'

import { selectFacet, unSelectFacet } from 'store/actions/facets'
import { selectSelectedFacets } from 'store/selectors/facets'
import { getPrimaryText, hasSelection, facetSort } from './helpers'

/**
 * Determines whether any element in the list, or children of list elements have a docCount
 * @param {Array} facetChildren An array of facets, generally the current items "children"
 * @returns {Boolean} True if any element, or any child has a docCount. False otherwise
 */
const hasDocCount = facetChildren => {
  return facetChildren.some(({ docCount, children }) => docCount || hasDocCount(children))
}

const FacetsNestedListItem = ({ id, facetType, name, items, docCount, indentation, parents }) => {
  const dispatch = useDispatch()
  const selectedFacets = useSelector(selectSelectedFacets)
  const isSelected = selectedFacets.some(
    ({ id: facetId, facetType: type }) => facetId === id && type === facetType
  )

  const itemsWithDocCount = items
    ?.filter(
      item =>
        item.docCount ||
        hasSelection(selectedFacets, item, facetType) ||
        (facetType === 'regions' && hasDocCount(item.children))
    )
    .sort((a, b) => facetSort(a, b, facetType, selectedFacets))

  const itemsWithSelection = items?.filter(item => hasSelection(selectedFacets, item, facetType))

  const hasChildren = itemsWithDocCount && itemsWithDocCount.length > 0

  const [expanded, setExpanded] = useState(itemsWithSelection && itemsWithSelection.length > 0)

  const onClick = () => {
    setExpanded(!expanded)
  }

  const onSelect = e => {
    e.stopPropagation()
    if (isSelected) {
      dispatch(unSelectFacet({ id }))
    } else {
      dispatch(selectFacet({ id, name, facetType, parents }))
    }
  }

  return (
    <>
      <ListItem disablePadding>
        <ListItemIcon>
          <Checkbox checked={isSelected} onChange={onSelect} onClick={e => e.stopPropagation()} />
        </ListItemIcon>
        <ListItemText className={hasChildren ? 'expandable' : ''} onClick={onSelect}>
          {getPrimaryText(name, docCount)}
        </ListItemText>
        {hasChildren && (
          <ListItemIcon>
            <Button className="expand" onClick={onClick}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </Button>
          </ListItemIcon>
        )}
      </ListItem>
      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List>
            {itemsWithDocCount.map(({ id: itemId, name: itemName, children, docCount }) => (
              <FacetsNestedListItem
                indentation={indentation + 1}
                key={itemId}
                id={itemId}
                facetType={facetType}
                name={itemName}
                items={children}
                docCount={docCount}
                parents={[...(parents || []), { id, name, facetType }]}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  )
}

FacetsNestedListItem.propTypes = {
  id: PropTypes.number.isRequired,
  facetType: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  parents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      facetType: PropTypes.string
    })
  )
}

FacetsNestedListItem.defaultProps = {
  parents: []
}

export default FacetsNestedListItem
